import React, { FormEvent, useRef, useState } from 'react';
import { Button, lighten, styled, TextField, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import Loading from 'components/loading/Loading';
import { api } from 'services/api';
import { toast } from 'react-toastify';

type ForgotPasswordValidation = {
  email?: string;
};

const Container = styled('div')({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: 420,
  maxWidth: 'calc(100% - 40px)',
  padding: 30,
  margin: 20,
  position: 'relative',
  borderRadius: 16,
  backgroundColor: lighten(theme.palette.primary.main, 0.9),
  boxShadow: theme.shadows[5],
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  textAlign: 'center',
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'center',
  paddingTop: theme.spacing(1),
}));

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<ForgotPasswordValidation>({});
  const inputRef = useRef<HTMLInputElement>(null);

  function sendResetEmail() {
    setLoading(true);

    api
      .post('/users/forgot-password', { email })
      .then(response => {
        toast.success(`${response.data.message || `Uma mensagem de e-mail foi enviada para ${email}`}`);
      })
      .catch(err => {
        if (err.response?.data?.error) {
          toast.error(err.response.data.error);
          return;
        }

        toast.error('Não foi possível enviar o e-mail de redefinição de senha');
      })
      .finally(() => setLoading(false));
  }

  function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setValidation({});

    const schema = yup.object().shape({
      email: yup.string().email('Informe um e-mail válido').required('Informe seu e-mail'),
    });

    schema
      .validate({ email })
      .then(sendResetEmail)
      .catch((err: yup.ValidationError) => {
        setValidation({ [err.path as string]: err.message });
        inputRef.current?.focus();
      });
  }

  return (
    <Container>
      <Content>
        {loading && <Loading />}
        <Header>
          <Typography variant="h5">Redefinir senha</Typography>
          <Typography variant="body2" color="textSecondary">
            Informe seu e-mail para receber o link de redefinição de senha.
          </Typography>
        </Header>

        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            inputRef={inputRef}
            label="E-mail"
            placeholder="Digite seu e-mail"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={!!validation.email}
            helperText={validation.email}
            autoComplete="email"
            fullWidth
            margin="normal"
          />
          <button type="submit" style={{ display: 'none' }} />
        </form>

        <Actions>
          <Button fullWidth variant="contained" disabled={loading} onClick={() => handleSubmit()}>
            Enviar link
          </Button>
          <Link to="/login">Voltar para o login</Link>
        </Actions>
      </Content>
    </Container>
  );
};

export default ForgotPassword;
